/**
 * Create form for an activity.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { createActivity } from '@/services/activities.service';
import type { CreateActivityDto } from '@/types/activity.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { activityConfigValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { ACTIVITY_TYPES_QUERY_KEY } from './activity-configs';
import {
  applyActivityFormField,
  buildActivitySubmitDto,
  emptyCreateActivityForm,
  fetchActivityTypes,
  mapActivityTypeOptions,
} from './activity-form-utils';

/** Props for {@link CreateActivityView}. */
interface CreateActivityViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for an activity. */
export const CreateActivityView: React.FC<CreateActivityViewProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = React.useState<CreateActivityDto>(emptyCreateActivityForm);
  const validation = useFormValidation(activityConfigValidationSchema);

  const { data: activityTypes = [] } = useQuery({
    queryKey: ACTIVITY_TYPES_QUERY_KEY,
    queryFn: fetchActivityTypes,
  });

  const activityTypeOptions = React.useMemo(
    () => mapActivityTypeOptions(activityTypes),
    [activityTypes],
  );

  const createMutation = useMutation({
    mutationFn: (dto: CreateActivityDto) => createActivity(dto),
    onSuccess: () => {
      toast.success('Activité créée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Patch one field and run the shared validation schema. */
  const handleFieldChange = (
    field: keyof CreateActivityDto,
    value: string | number | boolean | undefined,
  ) => {
    const updatedData = applyActivityFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, updatedData);
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then POST with a denormalized type label. */
  const handleSubmit = () => {
    if (!validation.validateForm(formData)) return;
    createMutation.mutate(buildActivitySubmitDto(formData, activityTypes));
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une activité"
      subtitle="Renseignez les informations de l'activité"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => handleFieldChange('code', e.target.value)}
            onBlur={() => handleBlur('code')}
            placeholder="Ex: AIDE_DEVOIRS_6EME"
            maxLength={50}
            required
            helperText="Code unique identifiant l'activité"
            state={validation.getFieldState('code')}
            error={validation.getFieldError('code')}
          />

          <Input
            label="Libellé"
            value={formData.label}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            onBlur={() => handleBlur('label')}
            placeholder="Ex: Aide aux devoirs niveau 6ème"
            maxLength={255}
            required
            helperText="Nom complet de l'activité"
            state={validation.getFieldState('label')}
            error={validation.getFieldError('label')}
          />
        </div>

        <Select
          label="Type d'activité"
          value={formData.activityTypeId}
          onChange={(value) => handleFieldChange('activityTypeId', value)}
          onBlur={() => handleBlur('activityTypeId')}
          options={activityTypeOptions}
          placeholder="Sélectionner un type"
          required
          state={validation.getFieldState('activityTypeId')}
          error={validation.getFieldError('activityTypeId')}
        />

        <Input
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          onBlur={() => handleBlur('description')}
          placeholder="Ex: Accompagnement scolaire"
          maxLength={500}
          state={validation.getFieldState('description')}
          error={validation.getFieldError('description')}
        />
      </div>
    </SlidePanelForm>
  );
};
